import type { ExportedMessageRepository } from "@assistant-ui/react";

const STORAGE_KEY = "wildstream:conversation:v1";

export type ConversationSnapshot = {
  conversationId: string;
  conversationLanguage: string | null;
  export: ExportedMessageRepository;
  savedAt: number;
};

function getStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadSnapshot(): ConversationSnapshot | null {
  const storage = getStorage();
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<ConversationSnapshot>;
    if (
      typeof parsed?.conversationId !== "string" ||
      !parsed.conversationId ||
      !parsed.export ||
      typeof parsed.export !== "object"
    ) {
      console.warn("[conversation-storage] snapshot shape invalid, clearing");
      clearSnapshot();
      return null;
    }
    return {
      conversationId: parsed.conversationId,
      conversationLanguage:
        typeof parsed.conversationLanguage === "string" ? parsed.conversationLanguage : null,
      export: parsed.export as ExportedMessageRepository,
      savedAt: typeof parsed.savedAt === "number" ? parsed.savedAt : 0,
    };
  } catch {
    console.warn("[conversation-storage] snapshot unreadable, clearing");
    clearSnapshot();
    return null;
  }
}

export function saveSnapshot(record: ConversationSnapshot): void {
  const storage = getStorage();
  if (!storage) return;
  const payload = JSON.stringify(record);

  try {
    storage.setItem(STORAGE_KEY, payload);
    return;
  } catch (err) {
    if (!isQuotaError(err)) {
      console.warn("[conversation-storage] setItem failed", err);
      return;
    }
  }

  // Quota exceeded — prune the oldest half of the exported messages and retry.
  const pruned = pruneExport(record.export);
  if (!pruned) {
    clearSnapshot();
    console.warn("[conversation-storage] quota exceeded, dropping snapshot");
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ ...record, export: pruned }));
    console.warn("[conversation-storage] quota exceeded, pruned snapshot to fit");
  } catch {
    clearSnapshot();
    console.warn("[conversation-storage] quota exceeded, dropping snapshot after prune");
  }
}

export function clearSnapshot(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — caller already wants the record gone.
  }
}

function pruneExport(repo: ExportedMessageRepository): ExportedMessageRepository | null {
  const messages = repo.messages;
  if (!Array.isArray(messages) || messages.length <= 2) return null;
  const keepFrom = Math.floor(messages.length / 2);
  const kept = messages.slice(keepFrom);
  // Re-root the kept chain so the first retained message has no parent; this
  // matches ExportedMessageRepository's invariant that parentId points to a
  // previously-listed message or null.
  if (kept.length === 0) return null;
  const first = kept[0]!;
  kept[0] = { ...first, parentId: null };
  return {
    ...(repo.headId !== undefined ? { headId: repo.headId } : undefined),
    messages: kept,
  };
}

function isQuotaError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return (
    err.name === "QuotaExceededError" ||
    err.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    // Safari throws a generic DOMException with code 22 for quota.
    (err as { code?: number }).code === 22
  );
}
